import {useIntl} from '@pinecast/i18n';
import styled, {FOCUS_RING_WIDTH, focusVisibleRing} from '@pinecast/styles';
import * as React from 'react';

import {useLabelContext} from './LabelContext';
import type {ElementConfig} from './types';

// The first and last cells of a row keep room at the edge of the table for
// the focus ring of a control in them (a Details button, say), which is 2px
// wide and 2px out. Body cells hide their overflow, and so does the table's
// scroll wrapper, so with no padding they cut the ring off.
const EDGE_PADDING = FOCUS_RING_WIDTH * 2;

// When the table is wider than the wrapper, the wrapper takes focus, so that
// the arrow keys can scroll it (WCAG 2.1.1). Its ring goes inside its edge:
// the wrapper is as wide as its parent, which can hide what is outside it.
// There the ring meets the surface the table is on, as a cell's does.
const TableWrapper = styled('div', (_, designColors) => ({
  width: '100%',
  overflowX: 'auto',
  ...focusVisibleRing(designColors, -FOCUS_RING_WIDTH),
}));
const TableInner = styled(
  'table',
  (_, designColors) => ({
    border: 0,
    borderBottom: `1px solid ${designColors.borderGrayNormal}`,
    borderCollapse: 'collapse',
    lineHeight: '1.5',
    marginBottom: 20,
    width: '100%',
  }),
  {className: 'Table'},
);

// Whether the element is narrower than what is in it, so that it scrolls
// sideways. It measures again when the element or the table in it changes
// size: a narrower window, a longer translation, rows that load.
const useScrollsSideways = (ref: React.RefObject<HTMLElement | null>) => {
  const [scrolls, setScrolls] = React.useState(false);
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const measure = () => {
      setScrolls(element.scrollWidth > element.clientWidth);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => {
        window.removeEventListener('resize', measure);
      };
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (element.firstElementChild) {
      observer.observe(element.firstElementChild);
    }
    return () => {
      observer.disconnect();
    };
  }, [ref]);
  return scrolls;
};

export const Table = ({
  children,
  focusGroup,
  ...rest
}: ElementConfig<typeof TableInner> & {
  // Names the rows as a focus group, so that focus stays in the table when a
  // row is deleted (see `FocusRecoveryProvider`). By default the name is this
  // table's own. Give it a name when the table mounts again as it reloads,
  // so that focus can go back into it.
  focusGroup?: string;
}) => {
  const intl = useIntl();
  const id = React.useId();
  const label = useLabelContext();
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const scrolls = useScrollsSideways(wrapperRef);

  // A wrapper that scrolls is a region that a keyboard user can reach. Its
  // name is the table's: the `Label` around it, or the table's own name. A
  // table with neither gets a general one, so that the region is not
  // announced with no name.
  const ownName: string | undefined = rest['aria-label'];
  const ownLabelledBy: string | undefined = rest['aria-labelledby'];
  const labelledBy = label?.labelledBy ?? ownLabelledBy;
  const region = scrolls
    ? {
        'aria-label': labelledBy
          ? undefined
          : (ownName ??
            intl.formatMessage({
              description:
                'Accessibility label for a table that is wider than the screen and scrolls sideways, when the table has no name of its own',
              defaultMessage: 'Scrollable table',
            })),
        'aria-describedby': label?.describedBy,
        'aria-labelledby': labelledBy,
        role: 'region',
        tabIndex: 0,
      }
    : {};

  return (
    <TableWrapper {...region} ref={wrapperRef}>
      <TableInner data-focus-group={focusGroup ?? `table${id}`} {...rest}>
        {children}
      </TableInner>
    </TableWrapper>
  );
};
export const TableHeaderCell = styled('th', (_, designColors) => ({
  borderBottom: `1px solid ${designColors.borderGrayNormal}`,
  color: designColors.headingBlue,
  fontSize: 12,
  lineHeight: 26,
  paddingTop: 0,
  paddingBottom: 0,
  paddingRight: 8,
  paddingLeft: 8,
  textAlign: 'left',
  textTransform: 'uppercase',
  ':first-of-type': {
    paddingLeft: EDGE_PADDING,
  },
  ':last-of-type': {
    paddingRight: EDGE_PADDING,
  },
}));
export const TableBodyCell = styled(
  'td',
  ({$wrapAt}: {$wrapAt?: number}, designColors) => ({
    borderBottom: `1px solid ${designColors.borderGrayNormal}`,
    fontSize: 14,
    maxWidth: $wrapAt,
    overflow: 'hidden',
    paddingTop: 8,
    paddingBottom: 8,
    paddingRight: 8,
    paddingLeft: 8,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ':first-of-type': {
      paddingLeft: EDGE_PADDING,
    },
    ':last-of-type': {
      paddingRight: EDGE_PADDING,
    },
  }),
);
export const TableFooterCell = styled(
  'td',
  ({$wrapAt}: {$wrapAt?: number}) => ({
    fontSize: 14,
    maxWidth: $wrapAt,
    overflow: 'hidden',
    paddingTop: 8,
    paddingBottom: 8,
    paddingRight: 8,
    paddingLeft: 8,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ':first-of-type': {
      paddingLeft: EDGE_PADDING,
    },
    ':last-of-type': {
      paddingRight: EDGE_PADDING,
    },
  }),
);
